import { usePathname, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import { useEffect } from 'react';

/**
 * Global onboarding gate (Wave 58). Deep links — launcher shortcuts,
 * share-sheet intake, notification taps — land directly on app pages
 * and skip the boot check in app/index.tsx. A brand-new user long-
 * pressing the icon and tapping "Log expense" was dropped into an
 * empty expense form with no home to attach it to. This hook watches
 * every route change: no completed home + a non-exempt path → redirect
 * to the onboarding landing page.
 */

// Paths reachable WITHOUT a home: the onboarding flow itself, the
// public privacy policy (Play listing links to it), and the boot root.
const EXEMPT_PATHS = new Set([
  '/',
  '/start',
  '/welcome',
  '/path',
  '/home-type',
  '/year',
  '/size',
  '/hvac',
  '/inside',
  '/property',
  '/lifestyle',
  '/detail',
  '/generating',
  '/plan',
  '/privacy',
]);

// Once a completed home is seen, skip all further queries this session
// (homes are never hard-deleted down to zero mid-session in practice).
let knownOnboarded = false;

export function useOnboardingGate() {
  const db = useSQLiteContext();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (knownOnboarded) return;
    if (EXEMPT_PATHS.has(pathname)) return;
    let cancelled = false;
    (async () => {
      try {
        const row = await db.getFirstAsync<{ n: number }>(
          'SELECT COUNT(*) AS n FROM property WHERE setup_complete = 1 AND deleted_at IS NULL'
        );
        if (cancelled) return;
        if ((row?.n ?? 0) > 0) {
          knownOnboarded = true;
          return;
        }
        router.replace('/(onboarding)/start' as never);
      } catch {
        // DB not ready yet — the next navigation re-runs the check.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname, db, router]);
}
